/*
  Dasom Skin Scan (standalone storefront widget) - Dasom-branded interactive skin scan.

  Layer 1 (most customers): INTERACTIVE guided capture via DasomScanCoach (live lighting +
  face-positioning coaching, tap-to-capture burst - never fires on its own) -> on-device read (window.KijesticSkinAI,
  tone-invariant) -> per-metric rating bars + a regional problem-area map ("here's where") +
  Dasom product/bundle recommendation by the lowest-scoring metric, with in-widget shopping.

  Layer 2 (optional, deeper): DasomDeepAnalysis questionnaire (age, skin type/tone, routine,
  past treatments, biggest complaint, budget) + the consented scan photo -> Gemini-vision deep
  read with budget-aware recs. Set window.DASOM_AI_ENDPOINT to enable it.

  Optional personalized before/after render (window.DASOM_RENDER_ENDPOINT): opt-in, one render
  per click, with a prominent "simulation, not guaranteed" disclaimer.

  Scan analysis is on-device. Only the deep analysis (with consent) and the before/after render
  upload a photo, and the consent copy says so. Launcher sits bottom-LEFT so it does not collide
  with the chat concierge bubble (bottom-right).
*/
(function () {
  'use strict';
  var SHOP = (window.DASOM_SHOP || '').replace(/\/$/, '');
  var RENDER_EP = window.DASOM_RENDER_ENDPOINT || '';
  var COLLECT_EP = window.DASOM_COLLECT_ENDPOINT || '';
  // Klaviyo lead capture (public site ID + list ID are safe client-side by design).
  var KLAVIYO_KEY = window.DASOM_KLAVIYO_KEY || '';
  var KLAVIYO_LIST = window.DASOM_KLAVIYO_LIST || '';
  // In-widget shopping works only on the live Shopify storefront (same-origin AJAX cart).
  var CART_OK = (function () {
    try { return !SHOP || /myshopify\.com|dasomessence\.com/i.test(location.host) || location.host === new URL(SHOP || 'https://x').host; }
    catch (e) { return false; }
  })();
  function link(h) { return SHOP + '/products/' + h; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]; }); }
  // Dual-currency price string, matching the storefront's region-aware display:
  // US shoppers see US$ first, Canadians see CA$ first (same rules as dasom-dual-price.js).
  function dzUS() {
    try { var r = localStorage.getItem('dz_region'); if (r === 'US' || r === 'CA') return r === 'US'; } catch (e) {}
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      return tz === 'Pacific/Honolulu' || /^America\/(New_York|Chicago|Denver|Phoenix|Los_Angeles|Anchorage|Detroit|Boise|Juneau|Sitka|Nome|Menominee|Metlakatla|Yakutat|Adak|Indiana\/|Kentucky\/|North_Dakota\/)/.test(tz);
    } catch (e) { return false; }
  }
  function dualPrice(cad) {
    var n = parseFloat(cad);
    if (isNaN(n)) return '$' + esc(cad);
    var rate = parseFloat(window.DASOM_USD_RATE) || 0.73;
    var ca = 'CA$' + n.toLocaleString('en-CA'), us = 'US$' + Math.round(n * rate).toLocaleString('en-US');
    return dzUS() ? us + ' <span class="dz-usd">/ ' + ca + '</span>' : ca + ' <span class="dz-usd">/ ' + us + '</span>';
  }

  // Resolve a product handle to its first available variant id via the storefront product JSON.
  var variantCache = {};
  function getVariant(handle) {
    if (variantCache[handle]) return Promise.resolve(variantCache[handle]);
    return fetch((SHOP || '') + '/products/' + handle + '.js').then(function (r) { return r.json(); }).then(function (p) {
      var v = (p.variants || []).find(function (x) { return x.available; }) || (p.variants || [])[0];
      if (v) variantCache[handle] = v.id;
      return v ? v.id : null;
    }).catch(function () { return null; });
  }
  // Add a product to the Shopify cart via AJAX. Returns a promise.
  function addToCart(handle) {
    return getVariant(handle).then(function (id) {
      if (!id) throw new Error('no variant');
      return fetch((SHOP || '') + '/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      }).then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); });
    });
  }
  // Opt-in, consented collection of the scan photo + scores for improving the model (data flywheel).
  function collect(image, metrics, concernKey) {
    if (!COLLECT_EP || !image) return;
    try {
      fetch(COLLECT_EP, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: image, metrics: metrics, concern: concernKey, consent: true, source: location.host, ts: Date.now() })
      }).catch(function () {});
    } catch (e) {}
  }

  // Normalize a typed phone number to E.164 (North-America default). '' = unusable as-typed.
  function normPhone(p) {
    var s = String(p || '').trim();
    if (!s) return '';
    if (s.charAt(0) === '+') { var e = '+' + s.slice(1).replace(/\D/g, ''); return /^\+\d{8,15}$/.test(e) ? e : ''; }
    var d = s.replace(/\D/g, '');
    if (d.length === 10) return '+1' + d;
    if (d.length === 11 && d.charAt(0) === '1') return '+' + d;
    return '';
  }
  // Subscribe a consented scan lead to the Klaviyo list (express consent checkbox in the UI).
  // Channel consent is implied by the identifiers sent on this endpoint (revision 2024-10-15).
  // Falls back to email-only if the phone payload is rejected so a bad number never loses the lead.
  function klaviyoSubscribe(email, phoneE164, rawPhone, props) {
    function payload(withPhone) {
      var attrs = { email: email, properties: props };
      if (withPhone && phoneE164) { attrs.phone_number = phoneE164; }
      else if (rawPhone) { attrs.properties = Object.assign({}, props, { 'Scan Phone (raw)': rawPhone }); }
      return JSON.stringify({
        data: {
          type: 'subscription',
          attributes: { custom_source: 'Dasom Skin Scan', profile: { data: { type: 'profile', attributes: attrs } } },
          relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST } } }
        }
      });
    }
    function post(body) {
      return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(KLAVIYO_KEY), {
        method: 'POST', headers: { 'Content-Type': 'application/json', revision: '2024-10-15' }, body: body
      });
    }
    return post(payload(true)).then(function (r) {
      if (r.ok) return true;
      if (!phoneE164) throw new Error('subscribe failed');
      return post(payload(false)).then(function (r2) { if (!r2.ok) throw new Error('subscribe failed'); return true; });
    });
  }

  var P = {
    glowwash: { n: 'Glow Wash Brightening Foam Cleanser', h: 'glow-wash-brightening-foam-cleanser', price: 59 },
    glowtone: { n: 'Glowtone Synergy Toner', h: 'glowtone-synergy-toner', price: 179 },
    glowb20: { n: 'Glow B20 Brightening Concentrate', h: 'glow-b20-brightening-concentrate', price: 189 },
    lumelift: { n: 'Lume Lift Serum', h: 'lume-lift-serum', price: 189 },
    luxecell: { n: 'Luxecell Revive Cream', h: 'luxcell-revive-cream', price: 198 },
    vivaglow: { n: 'Viva Glow Brightening Cream', h: 'viva-glow-brightening-cream', price: 198 }
  };
  var CONCERN = {
    radiance: { label: 'dullness', hero: 'vivaglow', routine: ['glowwash', 'glowtone', 'vivaglow'], bundle: { n: 'The Glow Collection', h: 'the-glow-collection', price: 575 }, look: 'a brighter, more radiant, well-hydrated glow' },
    smoothness: { label: 'uneven texture', hero: 'lumelift', routine: ['glowwash', 'lumelift', 'luxecell'], bundle: { n: 'The Firm & Lift Collection', h: 'the-firm-amp-lift-collection', price: 349 }, look: 'smoother, firmer-looking, more refined skin texture' },
    evenness: { label: 'uneven tone', hero: 'glowb20', routine: ['glowwash', 'glowb20', 'vivaglow'], bundle: { n: 'The Bright Radiance Collection', h: 'the-bright-radiance-collection', price: 349 }, look: 'a more even, balanced skin tone' },
    clarity: { label: 'the look of dark spots', hero: 'glowb20', routine: ['glowwash', 'glowb20', 'vivaglow'], bundle: { n: 'The Clarify & Repair Collection', h: 'the-clarify-repair-collection', price: 235 }, look: 'clearer-looking skin with softened dark spots' }
  };

  var modal = document.getElementById('dssModal');
  var stage = document.getElementById('dssStage');
  var launch = document.getElementById('dssLaunch');
  var xbtn = document.getElementById('dssX');
  if (!modal || !stage || !launch) return;
  var coach = null, lastShot = null, lastZones = null, lastAn = null, lastConcernKey = null;

  function openScan() {
    modal.classList.add('open');
    if (window.DasomScanCoach) {
      coach = DasomScanCoach.run({
        stage: stage,
        onResult: function (shots, meta) { coach = null; lastZones = meta && meta.zones; analyzing(shots); },
        // Coach finished its own teardown (camera stopped); just hide the modal. No re-entry into coach.close().
        onClose: function () { coach = null; modal.classList.remove('open'); stage.innerHTML = ''; }
      });
    } else {
      stage.innerHTML = '<p class="dss-sub">Scan is loading. Please try again in a moment.</p>';
    }
  }
  function closeScan() { if (coach && coach.close) { try { coach.close(); } catch (e) {} } coach = null; modal.classList.remove('open'); stage.innerHTML = ''; }

  function analyzing(shots) {
    lastShot = shots[Math.floor(shots.length / 2)] || shots[0];
    var n = shots.length;
    stage.innerHTML = '<p class="dss-eyebrow">Analyzing</p><h3 class="dss-h">Reading ' + n + ' frame' + (n > 1 ? 's' : '') + '…</h3>' +
      '<div class="dss-view"><img src="' + lastShot + '" alt="Your scan"><div class="dss-line"></div></div>' +
      '<p class="dss-priv">Scoring your skin on your device.</p>';
    if (window.KijesticSkinAI) KijesticSkinAI.analyze(shots).then(function (an) { results(an); });
    else setTimeout(function () { results({ ok: false }); }, 1500);
  }

  // Build the regional problem-area map (on-device "here's where") for the captured photo.
  function renderRegionMap(holder) {
    if (!window.KijesticSkinAI || !KijesticSkinAI.analyzeRegions || !lastShot) return;
    KijesticSkinAI.analyzeRegions(lastShot, lastZones).then(function (rm) {
      if (!rm || !rm.ok || !holder) return;
      var focus = rm.zones.filter(function (z) { return z.band === 'focus'; }).map(function (z) { return z.label.toLowerCase(); });
      var zoneEls = rm.zones.map(function (z) {
        return '<div class="dss-zone dssz-' + z.band + '" style="left:' + (z.box[0] * 100) + '%;top:' + (z.box[1] * 100) + '%;width:' + (z.box[2] * 100) + '%;height:' + (z.box[3] * 100) + '%">' +
          (z.band === 'focus' ? '<span class="dss-zlab">' + esc(z.label) + '</span>' : '') + '</div>';
      }).join('');
      holder.innerHTML =
        '<div class="dss-rec-h">Your face map</div>' +
        '<div class="dss-map"><img src="' + lastShot + '" alt="your scan with zones">' + zoneEls + '</div>' +
        '<div class="dss-maplegend"><span class="dss-lg dssz-strong">Strong</span><span class="dss-lg dssz-good">Good</span><span class="dss-lg dssz-fair">Watch</span><span class="dss-lg dssz-focus">Focus</span></div>' +
        (focus.length ? '<p class="dss-priv">Most room to improve around your <b>' + esc(focus.join(' and ')) + '</b>.</p>' : '');
    });
  }

  function results(an) {
    lastAn = an;
    var bars = '', concernHtml = '', ringHtml = '', overall = 0, key = 'radiance';
    if (an && an.ok) {
      var lowestKey = an.metrics.slice().sort(function (a, b) { return a.score - b.score; })[0].key;
      overall = Math.round(an.metrics.reduce(function (s, x) { return s + x.score; }, 0) / an.metrics.length);
      // Inline centering: edge caches can serve older widget CSS for a few minutes after a
      // push, so the number's placement must not depend on which stylesheet variant loaded.
      ringHtml =
        '<div class="dss-ringwrap"><div class="dss-ring" id="dssRing" style="position:relative">' +
        '<span class="dss-ringnum" id="dssRingNum" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%)">0</span></div>' +
        '<div class="dss-ringlab">Dasom Glow Score<span>out of 100, read on your device</span></div></div>';
      function band(score, isLowest) {
        if (isLowest) return { t: 'Focus area', c: 'focus' };
        if (score >= 70) return { t: 'Strong', c: 'strong' };
        if (score >= 45) return { t: 'Good', c: 'good' };
        return { t: 'Fair', c: 'fair' };
      }
      bars = '<div class="dss-bars">' + an.metrics.map(function (x, i) {
        var bd = band(x.score, x.key === lowestKey);
        return '<div class="dss-bar" style="transition-delay:' + (i * 90) + 'ms"><span>' + x.label + '</span><div class="dss-track"><div class="dss-fill dssb-' + bd.c + '" data-v="' + x.score + '" style="transition-delay:' + (200 + i * 140) + 'ms"></div></div><span class="dss-score dssb-' + bd.c + '">' + bd.t + '</span></div>';
      }).join('') + '</div>';
      key = lowestKey;
      concernHtml = '<p class="dss-sub">Your scan flags <b>' + CONCERN[key].label + '</b> as the area with the most room to improve. Here is what we recommend.</p>';
    } else {
      concernHtml = '<p class="dss-sub">We could not get a clear read. Try brighter, even lighting and a clear face shot. Meanwhile, here is a brightening starting point.</p>';
    }
    lastConcernKey = key;
    var c = CONCERN[key], hero = P[c.hero];
    var recRows = c.routine.map(function (k) {
      var p = P[k];
      var buy = CART_OK
        ? '<button class="dss-add" data-h="' + p.h + '">Add to cart</button>'
        : '<a class="dss-add" href="' + link(p.h) + '" target="_blank" rel="noopener">Add to cart</a>';
      return '<div class="dss-item"><div class="dss-itxt"><span class="dss-n">' + p.n + '</span>' +
        '<span class="dss-pp">' + dualPrice(p.price) + ' · <a href="' + link(p.h) + '" target="_blank" rel="noopener">View</a></span></div>' + buy + '</div>';
    }).join('');
    var leadDone = false;
    try { leadDone = localStorage.getItem('dss_lead_done') === '1'; } catch (e) {}
    var leadHtml = '';
    if (KLAVIYO_KEY && KLAVIYO_LIST) {
      leadHtml = leadDone
        ? '<div class="dss-lead done"><div class="dss-lead-head">You’re on the list</div><p class="dss-lead-copy">Your results and routine guidance are headed to your inbox.</p></div>'
        : '<div class="dss-lead" id="dssLead">' +
          '<div class="dss-lead-head">Keep your results</div>' +
          '<p class="dss-lead-copy">Get your Glow Score and focus area by email with a simple routine plan, plus ingredient education and subscriber-only offers from Dasom.</p>' +
          '<input class="dss-in" id="dssLeadEmail" type="email" autocomplete="email" inputmode="email" placeholder="Email address">' +
          '<input class="dss-in" id="dssLeadPhone" type="tel" autocomplete="tel" inputmode="tel" placeholder="Phone (optional)">' +
          '<label class="dss-collect"><input type="checkbox" id="dssLeadConsent"> Yes, email me my scan results plus educational content about Dasom’s ingredients and products, and subscriber offers. Unsubscribe anytime.</label>' +
          '<p class="dss-priv" id="dssLeadSms" hidden>Adding your number means Dasom may text you occasional tips and offers once texting launches. Msg &amp; data rates may apply, reply STOP anytime.</p>' +
          '<button class="dss-btn" id="dssLeadGo" disabled>Email my results</button>' +
          '<div id="dssLeadMsg" class="dss-cartmsg" hidden></div>' +
          '</div>';
    }
    stage.innerHTML =
      '<p class="dss-eyebrow">Your results</p><h3 class="dss-h">Your skin, scored</h3>' +
      ringHtml + concernHtml + bars +
      '<div id="dssMap"></div>' +
      leadHtml +
      (window.DASOM_AI_ENDPOINT ? (
        '<div class="dss-deepcta"><div class="dss-deepcta-txt"><b>Want the full picture?</b> Answer a few quick questions for a deeper, personalised analysis and routine.</div>' +
        '<button class="dss-btn" id="dssDeep">Get my deep analysis</button></div>'
      ) : '') +
      '<div class="dss-rec-h">Recommended routine</div><div class="dss-rec">' + recRows + '</div>' +
      '<div id="dssCartMsg" class="dss-cartmsg" hidden></div>' +
      '<div class="dss-cta-row"><button class="dss-btn" id="dssAddAll">Add routine to cart</button>' +
      '<a class="dss-btn ghost" href="' + link(c.bundle.h) + '" target="_blank" rel="noopener">Shop the ' + c.bundle.n.replace('The ', '') + ' set</a></div>' +
      (COLLECT_EP ? (
        '<label class="dss-collect"><input type="checkbox" id="dssCollect"> Help improve Dasom\'s scan: store my photo securely to train our skincare AI. Optional, never shared, you can opt out anytime.</label>'
      ) : '') +
      (RENDER_EP ? (
        '<div class="dss-ba" id="dssBA">' +
        '<div class="dss-ba-head">See your possible glow</div>' +
        '<p class="dss-ba-copy">Want a preview of how your skin could look with <b>' + hero.n + '</b>? We can generate an illustrative before and after from your photo.</p>' +
        '<label class="dss-consent"><input type="checkbox" id="dssConsent"> I understand this sends my photo to Dasom\'s AI to create a simulated preview, and that it is an illustration, not a prediction or guarantee.</label>' +
        '<button class="dss-btn" id="dssGen" disabled>Generate my preview</button>' +
        '<p class="dss-disc">Simulation only. This is an artistic preview of a possible look, not actual results, not a prediction, and not guaranteed. Skincare results vary by person and are never instant. Not medical or treatment advice.</p>' +
        '</div>'
      ) : '') +
      '<p class="dss-disc">Dasom Skin Scan is a guidance tool, not a medical diagnosis. Individual results vary.</p>';
    setTimeout(function () { stage.querySelectorAll('.dss-fill').forEach(function (f) { f.style.width = f.getAttribute('data-v') + '%'; }); }, 60);

    // Glow Score reveal: count the number up while the gold ring sweeps to the score.
    var ringEl = document.getElementById('dssRing'), ringNum = document.getElementById('dssRingNum');
    if (ringEl && ringNum && overall) {
      var t0 = null, DUR = 1300;
      function ease(t) { return 1 - Math.pow(1 - t, 3); }
      function step(ts) {
        if (!t0) t0 = ts;
        var p = Math.min(1, (ts - t0) / DUR), v = Math.round(ease(p) * overall);
        ringNum.textContent = v;
        ringEl.style.setProperty('--p', (ease(p) * overall) + '');
        if (p < 1 && document.body.contains(ringEl)) requestAnimationFrame(step);
        else ringEl.classList.add('done');
      }
      setTimeout(function () { requestAnimationFrame(step); }, 250);
    }

    renderRegionMap(document.getElementById('dssMap'));

    // In-widget shopping: add-to-cart per product + add whole routine.
    var msg = document.getElementById('dssCartMsg');
    function showMsg(html) { if (!msg) return; msg.hidden = false; msg.innerHTML = html; }
    function bindAdd(btn) {
      if (btn.tagName !== 'BUTTON') return;
      btn.onclick = function () {
        var h = btn.getAttribute('data-h'); var old = btn.textContent;
        btn.textContent = 'Adding…'; btn.disabled = true;
        addToCart(h).then(function () {
          btn.textContent = 'Added ✓';
          showMsg('Added to cart. <a href="' + SHOP + '/cart" target="_blank" rel="noopener">View cart &rarr;</a>');
          setTimeout(function () { btn.textContent = old; btn.disabled = false; }, 2500);
        }).catch(function () { btn.textContent = old; btn.disabled = false; showMsg('Could not add. <a href="' + link(h) + '" target="_blank" rel="noopener">Open the product page &rarr;</a>'); });
      };
    }
    stage.querySelectorAll('.dss-add').forEach(bindAdd);
    var addAll = document.getElementById('dssAddAll');
    if (addAll) addAll.onclick = function () {
      addAll.textContent = 'Adding…'; addAll.disabled = true;
      Promise.all(c.routine.map(function (k) { return addToCart(P[k].h).catch(function () { return null; }); })).then(function () {
        addAll.textContent = 'Routine added ✓';
        showMsg('Your routine is in the cart. <a href="' + SHOP + '/cart" target="_blank" rel="noopener">Checkout &rarr;</a>');
      });
    };

    // Layer 2: deep analysis questionnaire.
    var deepBtn = document.getElementById('dssDeep');
    if (deepBtn && window.DasomDeepAnalysis) deepBtn.onclick = function () {
      DasomDeepAnalysis.open({
        stage: stage, lastShot: lastShot,
        scan: { metrics: an && an.metrics, zones: lastZones },
        addToCart: addToCart, link: link, cartOk: CART_OK, cartHref: SHOP + '/cart',
        onBack: function () { results(lastAn); }
      });
    };

    // Consented lead capture -> Klaviyo (email required, phone optional, checkbox = express consent).
    var leadGo = document.getElementById('dssLeadGo');
    if (leadGo) bindLeadCapture(overall, CONCERN[key].label);

    // Opt-in consented photo collection (data flywheel).
    var collectCb = document.getElementById('dssCollect');
    if (collectCb) collectCb.onchange = function () { if (collectCb.checked) collect(lastShot, an && an.metrics, key); };

    var consent = document.getElementById('dssConsent'), gen = document.getElementById('dssGen');
    if (consent && gen) { consent.onchange = function () { gen.disabled = !consent.checked; }; gen.onclick = function () { generatePreview(hero, c); }; }
  }

  // Wire the lead-capture card: consent gates the button, phone reveals the SMS note,
  // submit subscribes to Klaviyo with the scan context as profile properties.
  function bindLeadCapture(overall, focusLabel) {
    var emailIn = document.getElementById('dssLeadEmail'), phoneIn = document.getElementById('dssLeadPhone');
    var consent = document.getElementById('dssLeadConsent'), go = document.getElementById('dssLeadGo');
    var msg = document.getElementById('dssLeadMsg'), smsNote = document.getElementById('dssLeadSms');
    if (!emailIn || !consent || !go) return;
    function note(html) { if (msg) { msg.hidden = false; msg.innerHTML = html; } }
    consent.onchange = function () { go.disabled = !consent.checked; };
    if (phoneIn && smsNote) phoneIn.oninput = function () { smsNote.hidden = !phoneIn.value.trim(); };
    go.onclick = function () {
      var email = emailIn.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { note('Please enter a valid email address.'); emailIn.focus(); return; }
      var rawPhone = phoneIn ? phoneIn.value.trim() : '';
      var props = {
        'Skin Scan Glow Score': overall || null,
        'Skin Scan Focus Area': focusLabel || '',
        'Skin Scan Date': new Date().toISOString().slice(0, 10),
        'Skin Scan Source': location.host
      };
      go.disabled = true; go.textContent = 'Sending…';
      klaviyoSubscribe(email, normPhone(rawPhone), rawPhone, props).then(function () {
        try { localStorage.setItem('dss_lead_done', '1'); } catch (e) {}
        var lead = document.getElementById('dssLead');
        if (lead) lead.innerHTML = '<div class="dss-lead-head">You’re in ✓</div><p class="dss-lead-copy">Your results are on the way to <b>' + esc(email) + '</b>. Watch for your routine plan and ingredient guides from Dasom.</p>';
      }).catch(function () {
        go.disabled = false; go.textContent = 'Email my results';
        note('Something went wrong sending your details. Please try again.');
      });
    };
  }

  function generatePreview(hero, c) {
    if (!lastShot || !RENDER_EP) return;
    var ba = document.getElementById('dssBA');
    ba.innerHTML = '<div class="dss-ba-head">Generating your preview…</div><div class="dss-view"><img src="' + lastShot + '" alt="your photo"><div class="dss-line"></div></div><p class="dss-priv">Creating an illustrative preview. This takes a few seconds.</p>';
    fetch(RENDER_EP, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: lastShot, concern: c.label, look: c.look, product: hero.n })
    }).then(function (r) { return r.json(); }).then(function (d) {
      var after = d && (d.image || d.after);
      if (!after) throw new Error('no image');
      ba.innerHTML =
        '<div class="dss-ba-head">Your possible glow with ' + hero.n + '</div>' +
        '<div class="dss-ba-grid"><figure><img src="' + lastShot + '" alt="before"><figcaption>Now</figcaption></figure>' +
        '<figure><img src="' + esc(after) + '" alt="illustrative after"><figcaption>Illustrative preview</figcaption></figure></div>' +
        '<div class="dss-cta-row"><a class="dss-btn" href="' + link(hero.h) + '" target="_blank" rel="noopener">Shop ' + hero.n + '</a></div>' +
        '<p class="dss-disc">Simulation only. An artistic preview of a possible look, not actual results, not a prediction, and not guaranteed. Results vary by person and are never instant. Not medical or treatment advice.</p>';
    }).catch(function () {
      ba.innerHTML = '<div class="dss-ba-head">Preview unavailable</div><p class="dss-ba-copy">We could not generate a preview right now. Your scan results and routine above still apply.</p><div class="dss-cta-row"><a class="dss-btn" href="' + link(hero.h) + '" target="_blank" rel="noopener">Shop ' + hero.n + '</a></div>';
    });
  }

  launch.addEventListener('click', openScan);
  if (xbtn) xbtn.addEventListener('click', closeScan);
  modal.addEventListener('click', function (e) { if (e.target === modal) closeScan(); });
  window.openDasomScan = openScan;
})();
